import { useState } from "react"
import {
    Typography,
    Button,
    Card,
    Slider,
    Radio,
    Tooltip
} from "antd"
import {
    InfoCircleOutlined,
    CheckCircleOutlined,
} from "@ant-design/icons"

const { Title, Text } = Typography

const ThresholdControls = () => {
    const [urgency, setUrgency] = useState(5)
    const [impact, setImpact] = useState(5)
    const [confidence, setConfidence] = useState(5)
    const [selectedPreset, setSelectedPreset] = useState("default")

    const handlePresetChange = (e) => {
        const preset = e.target.value
        setSelectedPreset(preset)
        switch (preset) {
            case "default":
                setUrgency(5)
                setImpact(5)
                setConfidence(5)
                break
            case "cautious":
                setUrgency(7)
                setImpact(7)
                setConfidence(6)
                break
            case "aggressive":
                setUrgency(3)
                setImpact(3)
                setConfidence(4)
                break
            case "silent":
                setUrgency(9)
                setImpact(9)
                setConfidence(8)
                break
            default:
                break
        }
    }

    const handleSliderChange = (setter, value) => {
        setter(value)
        setSelectedPreset(null) // Deselect preset when sliders are changed manually
    }

    const resetToDefault = () => {
        setUrgency(5)
        setImpact(5)
        setConfidence(5)
        setSelectedPreset("default")
    }

    const presetOptions = [
        { value: "default", label: "Default", urgency: 5, impact: 5, confidence: 5 },
        { value: "cautious", label: "Cautious", urgency: 7, impact: 7, confidence: 6 },
        { value: "aggressive", label: "Aggressive", urgency: 3, impact: 3, confidence: 4 },
        { value: "silent", label: "Silent Mode", urgency: 9, impact: 9, confidence: 8 },
    ]

    return (
        <Card className="mb-6">
            <div className="flex justify-between items-center mb-4">
                <div>
                    <Title level={4}>Threshold Controls</Title>
                    <Text type="secondary">Set minimum levels for urgency, impact, and confidence.</Text>
                </div>
                <Button onClick={resetToDefault}>Reset to Default</Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <Radio.Group onChange={handlePresetChange} value={selectedPreset} className="w-full">
                    {presetOptions.map((preset) => (
                        <>
                            <Radio.Button
                                key={preset.value}
                                value={preset.value}
                                className={`w-full text-left mb-2 mt-2 rounded-lg border ${selectedPreset === preset.value ? "border-blue-500 bg-blue-50" : "border-gray-200"}`}
                                style={selectedPreset === preset.value ? { borderColor: "#3B82F6", backgroundColor: "#EFF6FF" } : {}}
                            >
                                <Text strong>{preset.label}</Text>
                            </Radio.Button>
                            <Text type="secondary" className="text-xs ml-4">
                                U: {preset.urgency} | I: {preset.impact} | C: {preset.confidence}
                            </Text>
                        </>
                    ))}
                </Radio.Group>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div>
                    <div className="flex items-center mb-2">
                        <Text strong>Urgency</Text>
                        <Text className="ml-2 text-gray-500">{urgency}/10</Text>
                        <Tooltip title="Defines how time-sensitive a signal is.">
                        <InfoCircleOutlined className="ml-2 text-gray-400" />
                        </Tooltip>
                    </div>
                    <Slider
                        min={0}
                        max={10}
                        onChange={(value) => handleSliderChange(setUrgency, value)}
                        value={urgency}
                    />
                </div>
                <div>
                    <div className="flex items-center mb-2">
                        <Text strong>Impact</Text>
                        <Text className="ml-2 text-gray-500">{impact}/10</Text>
                        <Tooltip title="Measures the potential positive or negative effect of a signal.">
                        <InfoCircleOutlined className="ml-2 text-gray-400" />
                        </Tooltip>
                    </div>
                    <Slider
                        min={0}
                        max={10}
                        onChange={(value) => handleSliderChange(setImpact, value)}
                        value={impact}
                    />
                </div>
                <div>
                    <div className="flex items-center mb-2">
                        <Text strong>Confidence</Text>
                        <Text className="ml-2 text-gray-500">{confidence}/10</Text>
                        <Tooltip title="Indicates the reliability of the signal detection.">
                        <InfoCircleOutlined className="ml-2 text-gray-400" />
                        </Tooltip>
                    </div>
                    <Slider
                        min={0}
                        max={10}
                        onChange={(value) => handleSliderChange(setConfidence, value)}
                        value={confidence}
                    />
                </div>
            </div>

            <Card className="bg-blue-50 border-blue-200">
                <div className="flex items-center">
                    <CheckCircleOutlined className="text-blue-500 mr-2" />
                    <Text strong className="text-blue-700">
                        17 signals currently meet or exceed your thresholds
                    </Text>
                    <Tooltip title="This number reflects the signals that would be flagged or executed based on your current threshold settings.">
                    <InfoCircleOutlined className="ml-2 text-blue-400" />
                    </Tooltip>
                </div>
            </Card>
        </Card>
    )
}

export default ThresholdControls